import type {
  RawData,
  TestDefinition,
  TestResult,
  ValidationResult,
} from '../types.ts';
import { invalid } from '../validation.ts';
import {
  errorText,
  formatHash,
  formatError,
  hash,
  result,
  stableJson,
  validation,
  validationError,
} from './shared.ts';

function webGlContexts(): {
  one: WebGLRenderingContext | null;
  two: WebGL2RenderingContext | null;
} {
  const oneCanvas = document.createElement('canvas');
  const twoCanvas = document.createElement('canvas');
  return {
    one: oneCanvas.getContext('webgl'),
    two: twoCanvas.getContext('webgl2'),
  };
}

async function glSample(
  context: WebGLRenderingContext | WebGL2RenderingContext | null,
): Promise<RawData> {
  if (!context) {
    return {
      pixelHash: '',
      attributes: {},
      extensions: [],
      limits: {},
      precision: {},
    };
  }
  context.clearColor(0.125, 0.5, 0.875, 1);
  context.clear(context.COLOR_BUFFER_BIT);
  const pixels = new Uint8Array(4);
  context.readPixels(0, 0, 1, 1, context.RGBA, context.UNSIGNED_BYTE, pixels);
  const limits = {
    maxTextureSize: Number(context.getParameter(context.MAX_TEXTURE_SIZE)),
    maxViewportDims: [
      ...(context.getParameter(context.MAX_VIEWPORT_DIMS) as Int32Array),
    ],
    maxVertexAttribs: Number(context.getParameter(context.MAX_VERTEX_ATTRIBS)),
  };
  const precisionValue = context.getShaderPrecisionFormat(
    context.FRAGMENT_SHADER,
    context.HIGH_FLOAT,
  );
  return {
    pixelHash: await hash([...pixels]),
    attributes: context.getContextAttributes() ?? {},
    extensions: (context.getSupportedExtensions() ?? []).sort(),
    limits,
    precision: precisionValue
      ? {
          rangeMin: precisionValue.rangeMin,
          rangeMax: precisionValue.rangeMax,
          precision: precisionValue.precision,
        }
      : {},
  };
}

async function collect(): Promise<RawData> {
  const contexts = webGlContexts();
  const first = {
    webgl1: await glSample(contexts.one),
    webgl2: await glSample(contexts.two),
  };
  const second = {
    webgl1: await glSample(contexts.one),
    webgl2: await glSample(contexts.two),
  };
  return { repeats: [first, second] };
}

function format(rawData: RawData): string {
  const error = formatError(rawData);
  if (error) {
    return error;
  }
  return formatHash(rawData);
}

async function run(): Promise<TestResult> {
  let rawData: RawData;
  try {
    rawData = await collect();
  } catch (error) {
    rawData = { error: errorText(error) };
  }
  return result(rawData, format);
}

function validate(rawData: RawData): ValidationResult {
  const error = validationError(rawData);
  if (error) {
    return error;
  }
  const repeats = Array.isArray(rawData.repeats) ? rawData.repeats : [];
  if (repeats.length != 2 || stableJson(repeats[0]) != stableJson(repeats[1])) {
    return invalid('WebGL output is not stable');
  }
  return validation([]);
}

export const webGlRenderingTest: TestDefinition = {
  id: 'webglRendering',
  title: 'WebGL rendering',
  category: 'shared',
  format,
  run,
  validate,
};
